import axios from 'axios';
import React, { useState, ReactNode } from 'react';
import Script from 'next/script';
import { Nav } from '@/components/nav';
import { Meta } from '@/components/meta';
import { GetServerSideProps } from 'next';
import { ShareURL } from '@/components/share-url';
import { PostOrPage } from 'tryghost__content-api';
import { DownloadApp } from '@/components/download-app';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import QRCode, { QRCodeSVG } from 'qrcode.react';
import {
  faBarsStaggered,
  faShareAlt,
  faX,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRouter } from 'next/router';
import PostCard from '@/components/post-card';
import '@splidejs/splide/dist/css/splide.min.css';
import { cn, formatTime, randomIndex, shareMessage } from '@/utils';
import { useCheerioModification, useMobileResponsive } from '@/hooks';
import dynamic from 'next/dynamic';
import { LazLoader } from '@/components/box-loader/LazLoader';
import { NewsletterForm } from '@/components/newsletter-form';
import AdBanner from '@/components/AdBanner';
import Head from 'next/head';
import { useSearch, SearchProvider } from '@/contexts/searchContext';
import * as cheerio from 'cheerio';
import MultiplexAdBanner from '@/components/MultiplexAdBanner';
// import Mahashivratri from '@/components/Blackfridaybar';
// import Blackfriday from '@/components/Blackfridaybar';
// import NewYear from '@/components/Newyearbar';

const FourZeroFour = dynamic(() => import(`../../404`), {
  ssr: true,
  loading: () => <LazLoader />,
});

const TableOfContent = dynamic(
  () => import(`../../../components/blogs-components/_TableOfContent`),
  {
    ssr: true,
    loading: () => <LazLoader />,
  },
);
const LanguageChange = dynamic(
  () => import(`../../../components/blogs-components/LanguageChange`),
  {
    ssr: true,
    loading: () => <LazLoader />,
  },
);

type ParsedContent = Document | null;

interface AudioData {
  src: string;
  title: string;
}

interface AudioSources {
  [key: number]: AudioData;
}

export default function BlogPage(props: {
  slug: string;
  post: PostOrPage;
  toc: { anchor: string; level: number; text: string }[];
  related_posts: PostOrPage[];
}) {
  const router = useRouter();
  const { query } = router;
  const { windowWidth } = useMobileResponsive();
  const [tocPopUpOpen, setTocPopUpOpen] = useState(false);
  const [referrer, setReferrer] = React.useState(`unknown`);
  const [isSpiritual, setIsSpiritual] = React.useState(false);
  const [isPremiumAppUser, setPremiumAppUser] = React.useState(false);
  const [translatedTOC, setTranslatedTOC] = React.useState<any>([]);
  const [translatedBlog, setTranslatedBlog] = React.useState(``);
  const [translatedTitle, setTranslatedTitle] = React.useState(``);
  const [hasNavigatorShare, setHasNavigatorShare] = React.useState(false);
  const [blogUtm, setBlogUtm] = React.useState(
    `https://lsm.lvl.fit?data=mp61447mj0fwyhi8xs6pfc`,
  );
  const [processedBlog, setProcessedBlog] = React.useState(``);

  const initialLanguage = query.lang || `en`;
  const [selectedLanguage, setSelectedLanguage] =
    React.useState(initialLanguage);
  const [audioData, setAudioData] = React.useState<AudioSources>({});

  const [faqsData, SetFaqsData] = React.useState<any>([]);

  const { setSearchText, searchBlog } = useSearch();

  // console.log(`post id`, props?.post?.id);

  React.useEffect(() => {
    // Update the URL with the selected language query parameter
    router.replace({
      pathname: router.pathname,
      query: { ...query, lang: selectedLanguage },
    });
  }, [selectedLanguage]);

  React.useEffect(() => {
    const languages = [`en`, `mr`, `hi`, `bn`, `kn`, `ta`, `te`, `gu`];
    languages.forEach((lang) => {
      const element = document.getElementById(lang);
      if (element) {
        element.style.display = lang === selectedLanguage ? `inline` : `none`;
        if (lang === selectedLanguage) {
          element.style.whiteSpace = `normal`;
        }
      }
    });
  });

  React.useEffect(() => {
    const audioElements = document.querySelectorAll(`.kg-audio-card`);

    const ids = [`en`, `hi`, `bn`, `ta`, `kn`, `mr`, `gu`];

    ids.forEach((id, index) => {
      if (index < audioElements.length) {
        (audioElements[index] as HTMLElement).id = id;
      }
    });

    if (audioElements.length === 1) {
      // If there's only one audio component, display it
      (audioElements[0] as HTMLElement).style.display = `flex`;
    } else {
      audioElements.forEach((element) => {
        const audioElement = element as HTMLElement;

        if (
          // (audioElement.id === selectedLanguage)
          (selectedLanguage === `te` && audioElement.id === `en`) ||
          audioElement.id === selectedLanguage
        ) {
          audioElement.style.display = `flex`;
        } else {
          // audioElement[0].style.display = 'flex';
          audioElement.style.display = `none`;
        }
      });
    }
  });

  React.useEffect(() => {
    setHasNavigatorShare(typeof window.navigator.share === `function`);
    const source = new URLSearchParams(window.location.search).get(`source`);
    if (source === `levelapppremium`) {
      setPremiumAppUser(true);
    }
    setReferrer(
      () => new URLSearchParams(window.location.search).get(`source`) || ``,
    );
  }, []);

  React.useEffect(() => {
    if (props.post.html) {
      const $ = cheerio.load(props.post.html);
      const data: AudioSources = {};
      $(`.kg-audio-card`).each((index, element) => {
        const audioSrc = $(element).find(`audio`).attr(`src`);
        const audioTitle = $(element).find(`.kg-audio-title`).text();
        if (audioSrc) {
          data[index] = {
            src: audioSrc,
            title: audioTitle || `Beginners series`, // Default title if not found
          };
        }
      });
      setAudioData(data);
    }
  }, [props.post.html]);

  React.useEffect(() => {
    const audioElements = document.querySelectorAll(
      `.kg-audio-player-container audio`,
    );

    audioElements.forEach((audioEl) => {
      const audioElem = audioEl as HTMLAudioElement;
      const parentElem = audioElem.parentElement;

      const playButton = parentElem?.querySelector(`.kg-audio-play-icon`);
      const pauseButton = parentElem?.querySelector(`.kg-audio-pause-icon`);
      const audioSlider = parentElem?.querySelector(
        `.kg-audio-seek-slider`,
      ) as HTMLInputElement;
      const currentTimeElement = parentElem?.querySelector(
        `.kg-audio-current-time`,
      );
      const durationElement = parentElem?.querySelector(`.kg-audio-duration`);

      const updateTimerDisplay = () => {
        if (currentTimeElement) {
          currentTimeElement.textContent = formatTime(audioElem.currentTime);
        }
        if (durationElement && !isNaN(audioElem.duration)) {
          durationElement.textContent = formatTime(audioElem.duration);
        }
      };

      const updateSlider = () => {
        if (audioSlider && !isNaN(audioElem.duration)) {
          const progress = (audioElem.currentTime / audioElem.duration) * 100;
          audioSlider.value = progress.toFixed(2);
        }
      };

      playButton?.addEventListener(`click`, () => {
        audioElem.play();
        playButton.classList.add(`kg-audio-hide`);
        pauseButton?.classList.remove(`kg-audio-hide`);
      });

      pauseButton?.addEventListener(`click`, () => {
        audioElem.pause();
        pauseButton.classList.add(`kg-audio-hide`);
        playButton?.classList.remove(`kg-audio-hide`);
      });

      audioElem.addEventListener(`loadedmetadata`, () => {
        updateTimerDisplay();
        updateSlider();
      });

      audioElem.addEventListener(`timeupdate`, () => {
        updateTimerDisplay();
        updateSlider();
      });

      audioSlider?.addEventListener(`input`, () => {
        const seekTime = parseFloat(audioSlider.value);
        audioElem.currentTime = (audioElem.duration * seekTime) / 100;
      });

      // Initial update
      updateTimerDisplay();
      updateSlider();
    });

    // Handle language change for audio
    // const audio1 = document.getElementById('audio1') as HTMLAudioElement;
    // if (audio1 && selectedLanguage !== 'en') {
    //   audio1.src = localAudio;
    // }
  });

  // For changing languages
  React.useEffect(() => {
    if (selectedLanguage === `en`) {
      setTranslatedTitle(``);
      setTranslatedBlog(``);
      setTranslatedTOC([]);
    }

    if (selectedLanguage != `en`) {
      const data = JSON.stringify({
        languageCode: selectedLanguage,
        blogTitle: props?.post?.title,
        blogHtml: props?.post?.html,
        blogID: props?.post?.id,
        blogTOC: props?.toc,
      });

      const config = {
        method: `post`,
        maxBodyLength: Infinity,
        url: `https://content-panel.lvl.fit/api/translateBlogs`, //Google translate
        // url: `http://localhost:4000/api/translateBlogs`, //Google translate
        headers: {
          'Content-Type': `application/json`,
        },
        data: data,
      };

      axios
        .request(config)
        .then((response) => {
          setTranslatedTitle(response.data.translatedTitle);
          setTranslatedBlog(response.data.translatedBlogText);
          setTranslatedTOC(response.data.translatedTOC);
        })
        .catch((error) => {
          //console.logerror);
        });
    }
  }, [selectedLanguage]);

  React.useEffect(() => {
    if (translatedBlog) {
      const $ = cheerio.load(translatedBlog);

      const baseURL = `https://mind.level.game/share/`;
      const buttonTexts = [
        `Try for Free `,
        `Download for Free `,
        `Unlock 7-day Free Premium`,
        `Get now for Free`,
        `Experience Now`,
        `Unlock Full Access Now `,
        `Unlock Premium for Free`,
      ];

      // Add 'nofollow' to appropriate links
      $(`a`).each((_, el) => {
        const href = $(el).attr(`href`);
        if (href && href.includes(baseURL)) {
          $(el).attr(`rel`, `nofollow`);
        }
      });

      // Add the audio player HTML elements back
      $(`.kg-audio-card`).each((index, element) => {
        const originalData = audioData[index] || ``;

        $(element).html(`
          <img src="" alt="audio-thumbnail" class="kg-audio-thumbnail kg-audio-hide">
          <div class="kg-audio-thumbnail placeholder">
            <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M7.5 15.33a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm-2.25.75a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0ZM15 13.83a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm-2.25.75a2.25 2.25 0 1 1 4.5 0 2.25 2.25 0 0 1-4.5 0Z"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M14.486 6.81A2.25 2.25 0 0 1 17.25 9v5.579a.75.75 0 0 1-1.5 0v-5.58a.75.75 0 0 0-.932-.727.755.755 0 0 1-.059.013l-4.465.744a.75.75 0 0 0-.544.72v6.33a.75.75 0 0 1-1.5 0v-6.33a2.25 2.25 0 0 1 1.763-2.194l4.473-.746Z"></path>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M3 1.5a.75.75 0 0 0-.75.75v19.5a.75.75 0 0 0 .75.75h18a.75.75 0 0 0 .75-.75V5.133a.75.75 0 0 0-.225-.535l-.002-.002-3-2.883A.75.75 0 0 0 18 1.5H3ZM1.409.659A2.25 2.25 0 0 1 3 0h15a2.25 2.25 0 0 1 1.568.637l.003.002 3 2.883a2.25 2.25 0 0 1 .679 1.61V21.75A2.25 2.25 0 0 1 21 24H3a2.25 2.25 0 0 1-2.25-2.25V2.25c0-.597.237-1.169.659-1.591Z"></path>
            </svg>
          </div>
          <div class="kg-audio-player-container">
            <audio src=${originalData.src} preload="metadata"></audio>
            <div class="kg-audio-title">${originalData.title}</div>
            <div class="kg-audio-player">
              <button class="kg-audio-play-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M23.14 10.608 2.253.164A1.559 1.559 0 0 0 0 1.557v20.887a1.558 1.558 0 0 0 2.253 1.392L23.14 13.393a1.557 1.557 0 0 0 0-2.785Z"></path>
                </svg>
              </button>
              <button class="kg-audio-pause-icon kg-audio-hide">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <rect x="3" y="1" width="7" height="22" rx="1.5" ry="1.5"></rect>
                  <rect x="14" y="1" width="7" height="22" rx="1.5" ry="1.5"></rect>
                </svg>
              </button>
              <input type="range" class="kg-audio-seek-slider" max="100" value="0">
              <button class="kg-audio-playback-rate">1×</button>
              <button class="kg-audio-unmute-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M15.189 2.021a9.728 9.728 0 0 0-7.924 4.85.249.249 0 0 1-.221.133H5.25a3 3 0 0 0-3 3v2a3 3 0 0 0 3 3h1.794a.249.249 0 0 1 .221.133 9.73 9.73 0 0 0 7.924 4.85h.06a1 1 0 0 0 1-1V3.02a1 1 0 0 0-1.06-.998Z"></path>
                </svg>
              </button>
              <button class="kg-audio-mute-icon kg-audio-hide">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M16.177 4.3a.248.248 0 0 0 .073-.176v-1.1a1 1 0 0 0-1.061-1 9.728 9.728 0 0 0-7.924 4.85.249.249 0 0 1-.221.133H5.25a3 3 0 0 0-3 3v2a3 3 0 0 0 3 3h.114a.251.251 0 0 0 .177-.073ZM23.707 1.706A1 1 0 0 0 22.293.292l-22 22a1 1 0 0 0 0 1.414l.009.009a1 1 0 0 0 1.405-.009l6.63-6.631A.251.251 0 0 1 8.515 17a.245.245 0 0 1 .177.075 10.081 10.081 0 0 0 6.5 2.92 1 1 0 0 0 1.061-1V9.266a.247.247 0 0 1 .073-.176Z"></path>
                </svg>
              </button>
              <input type="range" class="kg-audio-volume-slider" max="100" value="100">
            </div>
          </div>
        `);
      });

      // Add buttons based on the referrer

      if (referrer !== `levelapp` && referrer !== `levelapppremium`) {
        let buttonCount = 0;
        $(`h2`).each((_, ele) => {
          const buttonText = buttonTexts[buttonCount % buttonTexts.length];
          const button = `
            <a href="${blogUtm}" style="color: inherit; text-decoration: none;">
              <button
                style="
                  background: linear-gradient(to right, #44199A, #9D77EE);
                  color: white;
                  font-size: 0.98rem;
                  font-weight: 800;
                  padding: 0.75rem 1.25rem;
                  margin: 0.75rem 0;
                  border-radius: 1.5rem;
                  height: 3.5rem;
                  transition: all 0.5s;
                  display: inline-block;
                  transform-origin: center;
                "
                onmouseover="this.style.background='linear-gradient(to right, #9D77EE, #9D77EE, #44199A)'; this.style.transform='scale(1.05)';"
                onmouseout="this.style.background='linear-gradient(to right, #44199A, #9D77EE, #9D77EE)'; this.style.transform='scale(1)';"
              >
                ${buttonText}
              </button>
            </a>
          `;
          $(ele).before(button);
          buttonCount++;
        });
      }

      const updatedHtmlString = $.html();
      setProcessedBlog(updatedHtmlString);
    }
  }, [translatedBlog]);

  const { modifiedHtml } = useCheerioModification(
    props?.post?.html,
    blogUtm,
    selectedLanguage,
    referrer,
    props?.post?.id,
    isSpiritual,
  );

  const getIsSpiritual = async () => {
    const data = JSON.stringify({
      blog_id: props.post.id,
    });

    const config = {
      method: `get`,
      maxBodyLength: Infinity,
      url: `https://content-panel.lvl.fit/api/blogSpiritual/${props.post.id}`,
      headers: {
        'Content-Type': `application/json`,
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        setIsSpiritual(response.data.is_spiritual);
      })
      .catch((error) => {
        // console.log(error);
      });
  };

  // For getting the tracking Links
  const getUtmLink = () => {
    const data = JSON.stringify({
      blog_id: props.post.id,
    });

    const config = {
      method: `post`,
      maxBodyLength: Infinity,
      url: `https://content-panel.lvl.fit/api/getUtmWithBlogId`,
      headers: {
        'Content-Type': `application/json`,
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        setBlogUtm(response.data.utmlink);
      })
      .catch((error) => {
        // console.log(error);
      });
  };

  // this is extracting the question and answer from faqs of blog to make
  // mark up schema of the site fr SEO
  function extractFAQs(htmlString: string) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, `text/html`);

    const faqsHeading = doc.querySelector(`h2#frequently-asked-questions`);
    if (!faqsHeading) {
      console.error(`FAQs section not found`);
      return [];
    }

    const finalThoughtsHeading = doc.querySelector(`h2#final-thoughts`);

    const faqs = [];
    let currentElement = faqsHeading.nextElementSibling;
    let currentQuestion = null;
    let currentAnswer = [];

    while (currentElement && currentElement !== finalThoughtsHeading) {
      if (currentElement.tagName === `H3`) {
        // If we encounter a new question, push the previous Q&A to the list
        if (currentQuestion) {
          faqs.push({
            question: currentQuestion,
            answer: currentAnswer.join(` `),
          });
        }
        currentQuestion = currentElement.textContent?.trim() || ``;
        currentAnswer = [];
      } else if ([`P`, `UL`, `OL`].includes(currentElement.tagName)) {
        // Collect text content from paragraphs and lists
        if (currentElement.textContent) {
          currentAnswer.push(currentElement.textContent.trim());
        }
      }
      currentElement = currentElement.nextElementSibling;
    }

    // Push the last Q&A if it exists
    if (currentQuestion) {
      faqs.push({
        question: currentQuestion,
        answer: currentAnswer.join(` `),
      });
    }

    SetFaqsData(faqs);
  }

  // For setting Dynamic UTM links on each blog
  React.useEffect(() => {
    if (props.post.html) {
      extractFAQs(props.post.html);
    }
    getIsSpiritual();
    getUtmLink();
  }, []);

  // For share message
  const randomMessage = shareMessage[randomIndex].message.replace(
    `{Name}`,
    `${props?.post?.title}`,
  );

  if (!props.post) {
    return <FourZeroFour />;
  }

  const blogSchema = {
    '@context': `https://schema.org`,
    '@type': `BlogPosting`,
    mainEntityOfPage: {
      '@type': `WebPage`,
      '@id': `https://level.game/blogs/${props.post.slug}`,
    },
    url: `https://level.game/blogs/${props.post.slug}`,
    headline: props.post.title,
    image: props.post.feature_image,
    author: {
      '@type': `Organization`,
      name: `Level SuperMind`,
      url: `https://level.game/`,
    },
    publisher: {
      '@type': `Organization`,
      name: `Level Game`,
      logo: {
        '@type': `ImageObject`,
        url: `https://level.game/logo.png`,
      },
    },
    datePublished: props.post.published_at?.toString().split(`T`)[0],
    dateModified: props.post.updated_at?.toString().split(`T`)[0],
  };

  interface FAQ {
    question: string;
    answer: string;
  }

  const faqSchema = faqsData.length
    ? {
        '@context': `https://schema.org`,
        '@type': `FAQPage`,
        mainEntity: faqsData.map((faq: FAQ) => ({
          '@type': `Question`,
          name: faq.question,
          acceptedAnswer: {
            '@type': `Answer`,
            text: faq.answer,
          },
        })),
      }
    : null;

  return (
    <>
      <Head>
        <title>{props.post.title}</title>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(blogSchema) }}
        />
        {faqSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          />
        )}
      </Head>

      <div className="bg-[#E5D9FF]">
        {/*  {!isPremiumAppUser && <NewYear />} */}
        <Nav />
        {/* <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3436497941634374"
          crossOrigin="anonymous"
        /> */}

        <Meta
          title={props.post.title}
          desc={props.post.meta_description || undefined}
          image={props.post.feature_image || undefined}
          url={`https://level.game/blogs/${props.slug}`}
          canonical={`https://level.game/blogs/${props.post.slug}`}
        />

        <LanguageChange
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
        />
        <div
          className={cn(`max-w-[70%] mx-auto px-4 text-[#141414]`, {
            'max-w-3xl': windowWidth < 1270,
          })}
        >
          <div
            className={[
              `flex`,
              `flex-wrap`,
              `gap-2`,
              `mt-10`,
              `mb-4`,
              `font-bold`,
            ].join(` `)}
          >
            {props.post.tags?.map((tag) => (
              <button
                className="bg-white text-[#0F0622] p-2 rounded-3xl px-4"
                key={tag.slug}
                onClick={() => {
                  setSearchText(tag.name);
                  searchBlog(tag.name);
                  localStorage.setItem(`tagname`, tag.name!);
                  router.push(
                    {
                      pathname: `/blogs`,
                    },
                    undefined,
                    { shallow: true },
                  );
                }}
              >
                <small className="bg-white text-[#0F0622] p-2 rounded-3xl px-4">
                  {tag.name?.toUpperCase()}
                </small>
              </button>
            ))}
          </div>
          <div>
            <h1
              className={[`text-2xl`, `md:text-4xl`, `font-bold`, `mb-5`].join(
                ` `,
              )}
            >
              {/* if translation not selected give the clickbait title if clickbait title not there give normal title  */}
              {translatedTitle != `` ? translatedTitle : props.post.title}
            </h1>
          </div>
          {props.post.feature_image && (
            <img
              src={`${props.post.feature_image}`}
              alt="Blog Feature Image"
              className="max-w-full mb-4 block rounded-3xl"
            />
          )}

          <div className="md:flex md:justify-between block md:space-x-3">
            {windowWidth > 1270 && (
              <>
                <div className="w-full md:w-[30%] relative">
                  <div className="sticky top-24 mt-8  pr-9  w-80">
                    <div>
                      <TableOfContent
                        referrer={referrer}
                        selectedLanguage={selectedLanguage}
                        translatedTOC={translatedTOC}
                        toc={props.toc}
                      />
                      <hr className="border border-purple-400 m-4 " />
                    </div>

                    <div className="text-black p-4 space-y-3">
                      <span>
                        Enter your E-mail id for more such articles in your
                        inbox
                      </span>

                      <div className="">
                        <NewsletterForm />
                      </div>
                    </div>

                    <div className="m-4 p-4 text-center rounded-3xl bg-level-purple text-white ">
                      <strong className="mt-4">Build a Positive habit</strong>
                      <h4>Build a better life</h4>
                      <button className="bg-yellow-500 text-black py-2 m-3 px-3 font-bold rounded-3xl">
                        <a href="https://lsm.lvl.fit?data=guya8119qpa1psiuzwii">
                          Start my free trial
                        </a>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Mobile screen */}
            {windowWidth < 1270 && (
              <>
                <button
                  className="fixed bottom-12 right-7 w-12 h-12 bg-purple-500 text-white rounded-full"
                  onClick={() => setTocPopUpOpen(!tocPopUpOpen)}
                >
                  <FontAwesomeIcon
                    icon={faBarsStaggered}
                    className="text-2xl"
                  />
                </button>
                {tocPopUpOpen && (
                  <div
                    className={cn(
                      `fixed bottom-12 right-7 mt-8  pr-4 pb-4  w-80 bg-white z-50 rounded-lg`,
                    )}
                  >
                    <>
                      <button
                        className="absolute top-3  right-3 text-purple-600"
                        onClick={() => setTocPopUpOpen(!tocPopUpOpen)}
                      >
                        <FontAwesomeIcon icon={faX} />
                      </button>

                      <TableOfContent
                        referrer={referrer}
                        selectedLanguage={selectedLanguage}
                        translatedTOC={translatedTOC}
                        toc={props.toc}
                      />
                    </>
                  </div>
                )}
              </>
            )}

            <div
              className={cn(`w-full`, {
                'w-[70%] ': windowWidth > 1270,
              })}
            >
              <style>{`.prose a { color: #6826EA; font-weight: bold; text-decoration: underline }`}</style>

              {referrer === `levelapp` ? (
                <div
                  className="prose"
                  dangerouslySetInnerHTML={{
                    __html:
                      processedBlog != ``
                        ? processedBlog
                        : referrer === `levelapp`
                        ? modifiedHtml
                        : props.post.html || ``,
                  }}
                ></div>
              ) : (
                <div
                  className="prose"
                  dangerouslySetInnerHTML={{
                    __html:
                      processedBlog != `` ? processedBlog : modifiedHtml || ``,
                  }}
                ></div>
              )}

              {hasNavigatorShare ? (
                <button
                  className={[
                    `flex`,
                    `justify-center`,
                    `items-center`,
                    `bg-yellow-500`,
                    `rounded-xl`,
                    `shadow`,
                    `px-4`,
                    `py-2`,
                    `mt-4`,
                    `hover:shadow-lg`,
                    `transition-shadow`,
                  ].join(` `)}
                  onClick={() => {
                    window.navigator?.share({
                      url: `https://level.game/blogs/${props.post.slug}`,
                      text: randomMessage,
                      title: randomMessage,
                    });
                  }}
                >
                  <FontAwesomeIcon icon={faShareAlt} className="mr-2" /> Share
                  this article
                </button>
              ) : (
                <>
                  {referrer !== `levelapp` && (
                    <ShareURL
                      url={`https://level.game/blogs/${props.post.slug}`}
                      excerpt={
                        props.post.custom_excerpt ||
                        props.post.excerpt ||
                        undefined
                      }
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <AdBanner />
        </div>
        {referrer !== `levelapp` && referrer !== `levelapppremium` && (
          <DownloadApp blogUtm={blogUtm} />
        )}
        <div className="my-5 mx-auto w-full">
          <MultiplexAdBanner />
        </div>
        <div className="fixed bottom-5 right-5 z-50 bg-white p-2 rounded-xl shadow-lg">
          {router.query.source !== `levelapp` &&
            router.query.source !== `levelapppremium` && (
              <QRCodeSVG
                value={blogUtm}
                size={80}
                bgColor="#0F0622"
                fgColor="#FFFFFF"
              />
            )}
        </div>
        {referrer !== `levelapp` && (
          <>
            <Splide
              options={{
                type:
                  props.related_posts.filter(
                    (post) => post.slug !== props.post.slug,
                  ).length >= 3
                    ? `loop`
                    : `slide`,
                drag: true,
                perMove: 1,
                ////perPage: innerWidth > 900 ? 3 : 1,
                arrows: false,
                pagination: false,
                autoplay: true,
                interval: 2000,
                easing: `cubic-bezier(0, 0.55, 0.45, 1)`,
                //focus: `center`,
                pauseOnHover: true,
              }}
            >
              {props.related_posts
                .filter((post) => post.slug !== props.post.slug)
                .map((post) => (
                  <SplideSlide
                    key={post.slug}
                    className="max-w-[20rem] flex items-stretch gap-y-20"
                  >
                    <div className="px-2 mb-10">
                      <PostCard
                        post={post}
                        key={post.slug}
                        className="block h-full"
                      />
                    </div>
                  </SplideSlide>
                ))}
            </Splide>
          </>
        )}

        {/* <ChatBot /> */}
        {/* <DownloadFooterBlogs  blogUtm={blogUtm}/> */}
      </div>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const domain = context.req.headers.host;
  const slug = context.params?.slug as string | null;
  const proto = (context.req.headers[`x-forwarded-proto`] as string) || `http`;
  const hostname = domain?.startsWith(proto) ? domain : `${proto}://${domain}`;

  const raw = await fetch(`${hostname}/api/ghost-api?slug=${slug}`);
  const json = await raw.json();
  const toc = json.post?.toc || null;

  if (!json.post) {
    context.res.statusCode = 404;
  }

  const tag = json.post?.primary_tag?.slug;
  const relatedRaw = await fetch(`${hostname}/api/ghost-api?related=${tag}`);
  const relatedJson = await relatedRaw.json();

  return {
    props: {
      post: json.post,
      toc: toc,
      related_posts: relatedJson.posts || null,
      slug,
    },
  };
};
function useEffect(arg0: () => void, arg1: never[]) {
  throw new Error(`Function not implemented.`);
}
